/**
 * Dodge (Shift): a round slot after the action bar's G slot, with the charges and the recharge
 * sweep. Kept out of the ActionBar so the hotbar keys (and the latency-probe slot order) stay
 * the class's own.
 */
import type { CastResult, ContentRegistry, DeepReadonly, SimQueries, UnitId, World } from '@keystone/sim';
import { ABILITY } from '@keystone/content';
import { hudNow } from '../../game/prediction';
import { IconSlot } from '../kit/iconSlot';
import { type CooldownInput, type SlotView, computeCooldown, emptySlotView, usableFromReason } from '../kit/slotState';
import { type TooltipLive, actionTooltipModel } from '../tooltip/hud';

const DODGE = ABILITY.common.dodge;

/** The live slot (one HUD at a time), for instant press feedback from the key handler. */
let active: DodgeSlot | null = null;

/** Pressed visual on the Dodge slot right now (keydown, before the sim sees it). */
export function flashDodge(now: number): void {
  active?.view.press(now);
}

export class DodgeSlot {
  readonly view: IconSlot;
  private readonly sv: SlotView = emptySlotView();
  private readonly cdIn: CooldownInput = { now: 0, tickTime: 0, remainAtTick: 0, cd: undefined, defDurationMs: undefined, defCharges: undefined, gcdMs: 0, gcdStart: 0, gcdUntil: 0 };
  private tip: TooltipLive | null = null;
  private iconSet = false;

  constructor(parent: HTMLElement, onPress: () => void) {
    this.view = new IconSlot(parent, {
      size: 'md',
      className: 'action-dodge is-round',
      keybind: 'Shift',
      onPress: () => onPress(),
      tooltip: () => actionTooltipModel(this.tip, DODGE, 'Shift'),
    });
    active = this;
  }

  update(world: DeepReadonly<World>, content: ContentRegistry, queries: SimQueries, playerId: UnitId): void {
    const u = world.units[playerId];
    const a = content.ability(DODGE);
    if (!u || !a) return;
    if (!this.iconSet) {
      this.iconSet = true;
      this.view.setIcon(DODGE, { name: a.name, color: a.ui?.color });
    }
    if (this.tip) {
      this.tip.world = world;
      this.tip.content = content;
      this.tip.queries = queries;
      this.tip.playerId = playerId;
    } else this.tip = { world, content, queries, playerId };
    const cd = this.cdIn;
    cd.now = hudNow(world);
    cd.tickTime = world.time;
    let r: CastResult | null = null;
    try {
      cd.remainAtTick = queries.cooldownRemaining(playerId, DODGE);
      r = queries.canCast(playerId, DODGE, null, u.pos);
    } catch {
      cd.remainAtTick = 0;
    }
    cd.cd = u.cooldowns[DODGE];
    cd.defDurationMs = a.cooldown?.durationMs;
    cd.defCharges = a.cooldown?.charges;
    const v = computeCooldown(cd, this.sv);
    v.usable = usableFromReason(!!r?.ok, r && !r.ok ? r.reason : undefined);
    this.view.render(v, typeof performance !== 'undefined' ? performance.now() : 0);
  }

  dispose(): void {
    if (active === this) active = null;
  }
}
